// Выбор элементов библиотеки для текущего чата: chat-scoped стейт в
// draft-store + сверка с актуальной библиотекой и лимитом режима.

import { useCallback, useEffect, useMemo } from "react";

import { useWorkspaceElements } from "~/entities/element";
import { useActiveWorkspace } from "~/entities/workspace";
import { useDraftChatStore } from "~/shared/store/useDraftChatStore";

import { reconcileSelection, toggleElementId } from "./element-selection";

import type { Element } from "~/entities/element";

export interface UseGenerationElementsParams {
    chatId: string | null;
    /** `mode.maxElements`; null/0 — режим элементы не принимает. */
    maxElements: number | null;
}

export function useGenerationElements({ chatId, maxElements }: UseGenerationElementsParams) {
    const { activeWorkspaceId } = useActiveWorkspace();
    const limit = maxElements ?? 0;
    const supportsElements = limit > 0;

    const elementIdsByChat = useDraftChatStore((s) => s.elementIdsByChat);
    const setElementIds = useDraftChatStore((s) => s.setElementIds);
    const clearElementIds = useDraftChatStore((s) => s.clearElementIds);

    const selectedIds = useMemo(
        () => (chatId ? (elementIdsByChat[chatId] ?? []) : []),
        [elementIdsByChat, chatId],
    );

    // Библиотека нужна и без открытого пикера — чипы показывают имена, а сверка
    // отбрасывает элементы, удалённые из библиотеки в другой вкладке.
    const query = useWorkspaceElements(activeWorkspaceId ?? undefined, { enabled: supportsElements });
    const library = useMemo<Element[]>(() => query.data?.items ?? [], [query.data]);

    useEffect(() => {
        if (!chatId || selectedIds.length === 0) return;
        if (!supportsElements) {
            clearElementIds(chatId);
            return;
        }
        // Пока библиотека не загрузилась — не трогаем выбор (иначе сбросили бы
        // его на первом рендере и потеряли уже выбранные элементы).
        if (query.data === undefined) return;
        const next = reconcileSelection(
            selectedIds,
            library.map((e) => e.id),
            limit,
        );
        if (next.length !== selectedIds.length) setElementIds(chatId, next);
    }, [chatId, selectedIds, supportsElements, query.data, library, limit, setElementIds, clearElementIds]);

    const selectedElements = useMemo(
        () =>
            selectedIds
                .map((id) => library.find((element) => element.id === id))
                .filter((element): element is Element => element !== undefined),
        [selectedIds, library],
    );

    const setSelected = useCallback(
        (ids: string[]) => {
            if (chatId) setElementIds(chatId, ids.slice(0, limit));
        },
        [chatId, limit, setElementIds],
    );

    const toggle = useCallback(
        (elementId: string) => {
            if (chatId) setElementIds(chatId, toggleElementId(selectedIds, elementId, limit));
        },
        [chatId, limit, selectedIds, setElementIds],
    );

    const clear = useCallback(() => {
        if (chatId) clearElementIds(chatId);
    }, [chatId, clearElementIds]);

    return {
        supportsElements,
        limit,
        library,
        // Пустая библиотека и незагруженная выглядят одинаково — флаг отделяет их
        // для потребителей, которым важно «элемента нет» против «ещё не приехал».
        libraryLoaded: query.data !== undefined,
        selectedIds,
        selectedElements,
        setSelected,
        toggle,
        clear,
    };
}
